// components/SkeletonCard.tsx
import { type JSX } from 'react';
import SkeletonCard from './SkeletonCard';
import CONSTANTS from './SkeletonLoader.constants';
import styles from './SkeletonLoader.module.css';
import {
  SkeletonType,
  type ColumnSkeletonFunction,
  type ItemSkeletonFunction,
  type SkeletonOptions,
} from './SkeletonLoader.types';

const createSkeleton = (type: SkeletonType) => {
  switch (type) {
    case SkeletonType.SEARCH:
      return (): JSX.Element => (
        <div className={styles.searchContainer}>
          <SkeletonCard
            width={'100%'}
            height={'40px'}
            className={styles.search}
          />
        </div>
      );
    case SkeletonType.PRODUCT_CARD:
      return (): JSX.Element => (
        <div>
          <SkeletonCard width={'100%'} aspectRatio={3 / 4} />
          <SkeletonCard width={'100%'} height={'10px'} />
          <SkeletonCard width={'50%'} height={'10px'} />
        </div>
      );

    case SkeletonType.CURATION_CARD:
      return (): JSX.Element => (
        <div className={styles.curationContainer}>
          <SkeletonCard
            width={'100%'}
            aspectRatio={378 / 165}
            className={styles.curationBanner}
          />
          <SkeletonCard
            width={'100%'}
            height={'17px'}
            className={styles.curationHeading}
          />
          <SkeletonCard
            width={'50%'}
            height={'17px'}
            className={styles.curationInfo}
          />
        </div>
      );

    case SkeletonType.LIST:
      return (
        count: number = 1,
        options: SkeletonOptions = {}
      ): JSX.Element => {
        const {
          paddingBottom = CONSTANTS.PADDING_BOTTOM,
          paddingBetween = CONSTANTS.PADDING_BETWEEN,
          className = '',
          cardProps,
        } = options;

        return (
          <div
            className={`${styles.column} ${className}`}
            style={{ width: '100%' }}
          >
            {Array.from({ length: count }).map((_, index) => (
              <div
                key={`skeleton-col-${index}`}
                style={{
                  paddingBottom:
                    index === count - 1 ? paddingBottom : paddingBetween,
                  width: '100%',
                }}
              >
                {(
                  createSkeleton(
                    SkeletonType.PRODUCT_CARD
                  ) as ItemSkeletonFunction
                )(cardProps)}
              </div>
            ))}
          </div>
        );
      };

    case SkeletonType.DOUBLE_COLUMN:
      return (
        count: number = 1,
        options: SkeletonOptions = {}
      ): JSX.Element => {
        const { className = '', ...columnOptions } = options;
        const itemsPerColumn = Math.ceil(count / 2);

        return (
          <div className={className}>
            {(createSkeleton(SkeletonType.LIST) as ColumnSkeletonFunction)(
              itemsPerColumn,
              columnOptions
            )}
            {(createSkeleton(SkeletonType.LIST) as ColumnSkeletonFunction)(
              itemsPerColumn,
              columnOptions
            )}
          </div>
        );
      };

    case SkeletonType.TRIPLE_COLUMN:
      return (rowCount: number = 1): JSX.Element => {
        const cellCount = rowCount * 3;
        return (
          <div className={styles.gridThreeColumns}>
            {Array.from({ length: cellCount }).map((_, index) => (
              <SkeletonCard
                key={`skeleton-triple-${index}`}
                width="100%"
                aspectRatio={3 / 5}
              />
            ))}
          </div>
        );
      };

    case SkeletonType.PDP:
      return (): JSX.Element => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
            backgroundColor: '#f4f5f9',
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '480px',
              position: 'relative',
              minHeight: '100dvh',
              background: '#fff',
              overflowY: 'hidden',
              paddingBottom: '80px',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px 0 12px',
                backgroundColor: '#fff',
              }}
            >
              <SkeletonCard
                width={140}
                height={24}
                styleOverride={{ borderRadius: '6px', marginBottom: '6px' }}
              />
              <SkeletonCard
                width={120}
                height={12}
                styleOverride={{ borderRadius: '4px' }}
              />
            </div>

            <div
              style={{
                padding: '12px',
                borderRadius: '28px',
                margin: '0 12px 12px 12px',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: 'transparent',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  borderRadius: '24px',
                  overflow: 'hidden',
                }}
              >
                <SkeletonCard
                  width="100%"
                  height={360}
                  styleOverride={{ borderRadius: '24px' }}
                />
                <SkeletonCard
                  width={68}
                  height={84}
                  styleOverride={{
                    position: 'absolute',
                    bottom: '12px',
                    left: '12px',
                    borderRadius: '12px',
                    border: '2px solid rgba(255, 255, 255, 0.6)',
                  }}
                />
                <SkeletonCard
                  width={90}
                  height={24}
                  styleOverride={{
                    position: 'absolute',
                    bottom: '12px',
                    right: '12px',
                    borderRadius: '8px',
                  }}
                />
              </div>
            </div>

            <SkeletonCard
              width="100%"
              height={44}
              styleOverride={{ marginBottom: '12px', borderRadius: '0px' }}
            />

            <div style={{ padding: '0 16px 16px' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <SkeletonCard
                  width={100}
                  height={16}
                  styleOverride={{ borderRadius: '4px' }}
                />
                <SkeletonCard
                  width={16}
                  height={16}
                  styleOverride={{ borderRadius: '50%' }}
                />
              </div>

              <SkeletonCard
                width="85%"
                height={18}
                styleOverride={{
                  borderRadius: '4px',
                  marginTop: '10px',
                  marginBottom: '16px',
                }}
              />

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                }}
              >
                <SkeletonCard
                  width={80}
                  height={28}
                  styleOverride={{ borderRadius: '6px' }}
                />
                <SkeletonCard
                  width={50}
                  height={18}
                  styleOverride={{ borderRadius: '4px' }}
                />
                <SkeletonCard
                  width={60}
                  height={18}
                  styleOverride={{ borderRadius: '4px' }}
                />
              </div>

              <SkeletonCard
                width="100%"
                height={56}
                styleOverride={{
                  borderRadius: '12px',
                  marginBottom: '16px',
                }}
              />

              <div
                style={{
                  height: '1px',
                  backgroundColor: '#f4f5fa',
                  width: '100%',
                }}
              />

              <div style={{ padding: '16px 0 8px' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                  }}
                >
                  <SkeletonCard
                    width={35}
                    height={14}
                    styleOverride={{ borderRadius: '4px' }}
                  />
                  <SkeletonCard
                    width={110}
                    height={14}
                    styleOverride={{ borderRadius: '4px' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {[1, 2, 3, 4, 5].map((idx) => (
                    <SkeletonCard
                      key={idx}
                      width={40}
                      height={40}
                      styleOverride={{
                        borderRadius: '50%',
                      }}
                    />
                  ))}
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#f4f5fa',
                    width: '100%',
                    marginTop: '16px',
                  }}
                />
              </div>

              <div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '16px 0',
                  }}
                >
                  <SkeletonCard
                    width={100}
                    height={14}
                    styleOverride={{ borderRadius: '4px' }}
                  />
                  <SkeletonCard
                    width={12}
                    height={12}
                    styleOverride={{ borderRadius: '50%' }}
                  />
                </div>
                <div
                  style={{
                    height: '1px',
                    backgroundColor: '#f4f5fa',
                    width: '100%',
                  }}
                />
              </div>

              <div style={{ padding: '16px 0' }}>
                <SkeletonCard
                  width={160}
                  height={14}
                  styleOverride={{
                    borderRadius: '4px',
                    marginBottom: '12px',
                  }}
                />
                <div
                  style={{ display: 'flex', gap: '10px', overflowX: 'hidden' }}
                >
                  {[1, 2, 3].map((idx) => (
                    <SkeletonCard
                      key={idx}
                      width={110}
                      height={154}
                      styleOverride={{
                        borderRadius: '12px',
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div
              style={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '100%',
                maxWidth: '480px',
                height: '80px',
                backgroundColor: '#fff',
                borderTop: '1px solid #f4f5fa',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxSizing: 'border-box',
                zIndex: 10,
              }}
            >
              <SkeletonCard
                width={48}
                height={48}
                styleOverride={{
                  borderRadius: '50%',
                }}
              />
              <SkeletonCard
                width="calc(100% - 60px)"
                height={48}
                styleOverride={{
                  borderRadius: '24px',
                  flex: 1,
                }}
              />
            </div>
          </div>
        </div>
      );

    case SkeletonType.CUSTOM:
      return (
        count: number = 1,
        options: SkeletonOptions = {}
      ): JSX.Element => {
        return (
          createSkeleton(SkeletonType.DOUBLE_COLUMN) as ColumnSkeletonFunction
        )(count, options);
      };

    default:
      throw new Error(`Invalid skeleton type: ${type}`);
  }
};

const SkeletonLoader = {
  search: createSkeleton(SkeletonType.SEARCH),
  productCard: createSkeleton(
    SkeletonType.PRODUCT_CARD
  ) as ItemSkeletonFunction,
  list: createSkeleton(SkeletonType.LIST) as ColumnSkeletonFunction,
  doubleColumnList: createSkeleton(
    SkeletonType.DOUBLE_COLUMN
  ) as ColumnSkeletonFunction,
  tripleColumnList: createSkeleton(SkeletonType.TRIPLE_COLUMN),
  curationCard: createSkeleton(SkeletonType.CURATION_CARD),
  custom: createSkeleton(SkeletonType.CUSTOM) as ColumnSkeletonFunction,
  pdp: createSkeleton(SkeletonType.PDP) as ItemSkeletonFunction,
};

export default SkeletonLoader;
